import { useMemo, useState } from 'react'
import { Modal } from '@/components/common'
import TreeVisualization from './TreeVisualization'

/**
 * Branch shape used by the workspace. We accept a structurally typed
 * subset so callers can pass Convex docs without remapping.
 */
export interface ModalBranch {
  _id: string
  parentBranchId?: string | null
  name?: string
  thematicName?: string
  userRenamedName?: string
  branchType?: 'sequential' | 'parallel'
  status: string
  depth: number
  confidenceScore?: number
}

export interface TreeViewModalProps {
  isOpen: boolean
  onClose: () => void
  branches: ModalBranch[]
  activeBranchId: string | null
  onSetActive: (branchId: string) => void
  onRename: (branchId: string, name: string) => Promise<void>
  onTerminate: (branchId: string, reason?: string) => Promise<void>
  onPrune: (branchId: string) => Promise<void>
}

/**
 * Convert a flat list of Convex branch documents into the loose
 * `{nodes, edges}` shape the D3 `TreeVisualization` reads at runtime.
 *
 * Note: `TreeVisualization` was originally typed for a richer node
 * (label/branchId/etc) but at runtime only touches `id, name, status,
 * type, depth, confidenceScore`. We cast through `unknown` to silence
 * the strict typecheck without changing behavior.
 */
function treeFromBranches(branches: ModalBranch[]): {
  nodes: unknown[]
  edges: unknown[]
} {
  return {
    nodes: branches.map((b) => ({
      id: b._id,
      name:
        b.userRenamedName ||
        b.thematicName ||
        b.name ||
        `Branch ${b._id.slice(-6)}`,
      type: b.branchType ?? 'sequential',
      status:
        (b.status as
          | 'active'
          | 'terminated_natural'
          | 'terminated_manual'
          | 'pruned') ?? 'active',
      depth: b.depth,
      confidenceScore: b.confidenceScore,
    })),
    edges: branches
      .filter((b) => b.parentBranchId)
      .map((b) => ({
        source: b.parentBranchId as string,
        target: b._id,
        type: (b.branchType ?? 'sequential') as 'sequential' | 'parallel',
      })),
  }
}

const TreeViewModal = ({
  isOpen,
  onClose,
  branches,
  activeBranchId,
  onSetActive,
  onRename,
  onTerminate,
  onPrune,
}: TreeViewModalProps) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(activeBranchId)
  const data = useMemo(() => treeFromBranches(branches), [branches])
  const selected = useMemo(
    () => branches.find((b) => b._id === selectedNodeId) ?? null,
    [branches, selectedNodeId],
  )

  const closeAndClear = () => {
    setSelectedNodeId(activeBranchId)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeAndClear}
      title="Conversation Tree"
      description="Click a node to open its actions. Sequential branches go deeper; parallel branches explore lateral angles."
      size="full"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-4 h-[70vh]">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <TreeVisualization
            data={data as unknown as Parameters<typeof TreeVisualization>[0]['data']}
            selectedNodeId={selectedNodeId}
            onNodeClick={(id) => setSelectedNodeId(id)}
          />
        </div>

        <aside className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 overflow-y-auto bg-white dark:bg-slate-900">
          {!selected ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Click a branch in the tree to see its actions.
            </p>
          ) : (
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  {selected.userRenamedName ||
                    selected.thematicName ||
                    selected.name ||
                    `Branch ${selected._id.slice(-6)}`}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Status: <span className="font-medium">{selected.status}</span> ·
                  Depth: {selected.depth} ·
                  Type: {selected.branchType ?? 'sequential'}
                </p>
              </div>

              <button
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                onClick={() => {
                  onSetActive(selected._id)
                  closeAndClear()
                }}
              >
                Set Active &amp; Close
              </button>

              <button
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                onClick={async () => {
                  const next = window.prompt(
                    'Rename branch to:',
                    selected.userRenamedName ||
                      selected.thematicName ||
                      selected.name ||
                      '',
                  )
                  if (!next || !next.trim()) return
                  await onRename(selected._id, next.trim())
                }}
              >
                Rename…
              </button>

              {selected.status === 'active' && (
                <button
                  className="w-full px-3 py-2 rounded-lg border border-amber-300 text-sm text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                  onClick={async () => {
                    const reason = window.prompt('Termination reason (optional):', '') || undefined
                    await onTerminate(selected._id, reason)
                  }}
                >
                  Terminate
                </button>
              )}

              <button
                className="w-full px-3 py-2 rounded-lg border border-red-300 text-sm text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={async () => {
                  if (
                    !window.confirm(
                      `Prune this branch? Its messages will be deleted and it cannot be revived.`,
                    )
                  )
                    return
                  await onPrune(selected._id)
                }}
              >
                Prune
              </button>
            </div>
          )}
        </aside>
      </div>
    </Modal>
  )
}

export default TreeViewModal
